// Admin God-mode de la matriz de módulos (homologación 2026-07-01).
// Editor único y DB-driven: por ARQUETIPO (defaults) y por ORG (override).
// Habilitado solo en ucognet; los endpoints exigen thot_admin/superuser.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { SlidersHorizontal, Building2, Layers, Loader2, Save, Check, AlertTriangle } from 'lucide-react';

import {
  getMatrix, setArchetypeModules, getOrgModules, setOrgModules,
  type ModuleMatrix, type OrgModulesState,
} from '../api';

export function ModuleAdminPage() {
  const [matrix, setMatrix] = useState<ModuleMatrix | null>(null);
  const [tab, setTab] = useState<'archetype' | 'org'>('archetype');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getMatrix().then(setMatrix).catch((e) => setError(String(e.message || e)));
  }, []);

  if (error) {
    return (
      <div className="m-6 rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
        <AlertTriangle className="inline mr-2 text-red-500" size={16} />
        {error.includes('403') ? 'Requiere permisos god-mode (thot_admin/superuser).' : `Error: ${error}`}
      </div>
    );
  }
  if (!matrix) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="animate-spin mr-2" size={18} /> Cargando matriz…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-start gap-3">
        <SlidersHorizontal className="text-primary mt-1" size={26} />
        <div>
          <h1 className="text-xl font-semibold text-foreground">Admin de Módulos (God-mode)</h1>
          <p className="text-sm text-muted-foreground">
            Fuente única DB-driven: qué módulos ve cada arquetipo y cada org. El frontend obedece esto.
          </p>
        </div>
      </header>

      <div className="flex gap-2 border-b border-border">
        <TabBtn active={tab === 'archetype'} onClick={() => setTab('archetype')} icon={<Layers size={15} />} label="Por arquetipo" />
        <TabBtn active={tab === 'org'} onClick={() => setTab('org')} icon={<Building2 size={15} />} label="Por organización" />
      </div>

      {tab === 'archetype' ? <ArchetypeEditor matrix={matrix} /> : <OrgEditor matrix={matrix} />}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 -mb-px ${
        active ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground'
      }`}
    >
      {icon}{label}
    </button>
  );
}

// ── Editor por arquetipo ──────────────────────────────────────────────────────
function ArchetypeEditor({ matrix }: { matrix: ModuleMatrix }) {
  const archetypes = Object.keys(matrix.archetypes).sort();
  const [arch, setArch] = useState(archetypes[0] || '');
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setSel(new Set(matrix.archetypes[arch] || []));
    setSaved(false);
  }, [arch, matrix]);

  const catalogKeys = useMemo(
    () => Object.keys(matrix.catalog).sort((a, b) => matrix.catalog[a].localeCompare(matrix.catalog[b])),
    [matrix],
  );

  const toggle = (k: string) => {
    setSel((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await setArchetypeModules(arch, [...sel]);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <label className="text-sm text-muted-foreground">Arquetipo:</label>
        <select
          value={arch}
          onChange={(e) => setArch(e.target.value)}
          className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground"
        >
          {archetypes.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <SaveButton onClick={save} saving={saving} saved={saved} />
      </div>
      <ModuleGrid catalogKeys={catalogKeys} matrix={matrix} selected={sel} onToggle={toggle} />
    </div>
  );
}

// ── Editor por org ────────────────────────────────────────────────────────────
function OrgEditor({ matrix }: { matrix: ModuleMatrix }) {
  const [orgId, setOrgId] = useState('');
  const [state, setState] = useState<OrgModulesState | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const catalogKeys = useMemo(
    () => Object.keys(matrix.catalog).sort((a, b) => matrix.catalog[a].localeCompare(matrix.catalog[b])),
    [matrix],
  );

  const load = async () => {
    if (!orgId.trim()) return;
    setLoading(true); setErr(null); setSaved(false);
    try {
      const s = await getOrgModules(orgId.trim());
      setState(s);
      setSel(new Set(Object.entries(s.resolved).filter(([, v]) => v).map(([k]) => k)));
    } catch (e: any) {
      setErr(String(e.message || e)); setState(null);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (k: string) => {
    setSel((prev) => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n; });
    setSaved(false);
  };

  const save = async () => {
    if (!state) return;
    setSaving(true);
    try {
      // enviamos override explícito {key:bool} para todo el catálogo (on/off).
      const modules: Record<string, boolean> = {};
      catalogKeys.forEach((k) => { modules[k] = sel.has(k); });
      await setOrgModules(state.org_id, modules);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <input
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
          placeholder="org_id (ej. rentalmaq)"
          className="rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-foreground"
        />
        <button onClick={load} disabled={loading} className="rounded-lg bg-muted px-3 py-1.5 text-sm text-foreground disabled:opacity-50">
          {loading ? <Loader2 className="animate-spin" size={15} /> : 'Cargar'}
        </button>
        {state && <SaveButton onClick={save} saving={saving} saved={saved} />}
      </div>
      {err && <p className="text-xs text-muted-foreground"><AlertTriangle className="inline mr-1 text-red-500" size={13} />{err}</p>}
      {state && (
        <>
          <p className="text-xs text-muted-foreground">
            Arquetipo: <b className="text-foreground">{state.archetype || '—'}</b>. El override per-org gana sobre el default del arquetipo.
          </p>
          <ModuleGrid catalogKeys={catalogKeys} matrix={matrix} selected={sel} onToggle={toggle} />
        </>
      )}
    </div>
  );
}

// ── Grid de módulos compartido ────────────────────────────────────────────────
function ModuleGrid({
  catalogKeys, matrix, selected, onToggle,
}: {
  catalogKeys: string[]; matrix: ModuleMatrix; selected: Set<string>; onToggle: (k: string) => void;
}) {
  const alwaysOn = new Set(matrix.always_on);
  const devOnly = new Set(matrix.dev_only);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
      {catalogKeys.map((k) => {
        const locked = alwaysOn.has(k);
        const on = locked || selected.has(k);
        return (
          <button
            key={k}
            disabled={locked}
            onClick={() => onToggle(k)}
            className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
              on ? 'border-primary bg-primary/5' : 'border-border bg-card'
            } ${locked ? 'opacity-60 cursor-not-allowed' : 'hover:bg-muted'}`}
          >
            <span className="min-w-0">
              <span className="block truncate text-foreground">{matrix.catalog[k]}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {k}{locked ? ' · always-on' : ''}{devOnly.has(k) ? ' · dev' : ''}
              </span>
            </span>
            <span className={`shrink-0 rounded-full border w-4 h-4 flex items-center justify-center ${on ? 'bg-primary border-primary' : 'border-border'}`}>
              {on && <Check size={11} className="text-primary-foreground" />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function SaveButton({ onClick, saving, saved }: { onClick: () => void; saving: boolean; saved: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={saving}
      className="ml-auto flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
    >
      {saving ? <Loader2 className="animate-spin" size={15} /> : saved ? <Check size={15} /> : <Save size={15} />}
      {saved ? 'Guardado' : 'Guardar'}
    </button>
  );
}
